import { useState, useEffect, useRef } from 'react';
import './TextCaseConverter.css';

const TextCaseConverter = () => {
  const [text, setText] = useState('');
  const [copied, setCopied] = useState('');
  const [spellCheckEnabled, setSpellCheckEnabled] = useState(false);
  const [misspelledWords, setMisspelledWords] = useState([]);
  const [highlightedText, setHighlightedText] = useState('');
  const textareaRef = useRef(null);

  // Simple spell checker using a basic dictionary approach
  // In production, you'd use a proper spell-checking API
  const checkSpelling = async (textToCheck) => {
    if (!spellCheckEnabled || !textToCheck.trim()) {
      setMisspelledWords([]);
      setHighlightedText('');
      return;
    }

    // Extract words (alphanumeric only)
    const words = textToCheck.match(/\b[a-zA-Z]+\b/g) || [];
    const uniqueWords = [...new Set(words.map(w => w.toLowerCase()))];

    try {
      // Check each word using a dictionary API
      const checks = await Promise.all(
        uniqueWords.slice(0, 20).map(async (word) => { // Limit to 20 words for performance
          try {
            const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word}`);
            return {
              word,
              correct: response.ok
            };
          } catch {
            return { word, correct: true }; // Assume correct if API fails
          }
        })
      );

      const misspelled = checks
        .filter(check => !check.correct)
        .map(check => check.word);

      setMisspelledWords(misspelled);

      // Create highlighted version
      let highlighted = textToCheck;
      misspelled.forEach(word => {
        const regex = new RegExp(`\\b${word}\\b`, 'gi');
        highlighted = highlighted.replace(regex, `<mark class="misspelled">$&</mark>`);
      });
      setHighlightedText(highlighted);

    } catch (error) {
      console.error('Spell check error:', error);
    }
  };

  // Debounce spell check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (spellCheckEnabled) {
        checkSpelling(text);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [text, spellCheckEnabled]);

  const convertCase = (type) => {
    let result = '';
    
    switch(type) {
      case 'upper':
        result = text.toUpperCase();
        break;
      case 'lower':
        result = text.toLowerCase();
        break;
      case 'title':
        result = text.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
        break;
      case 'sentence':
        result = text.toLowerCase().replace(/(^\s*\w|[.!?]\s*\w)/g, (char) => char.toUpperCase());
        break;
      case 'camel':
        result = text.toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
          .replace(/^[A-Z]/, (char) => char.toLowerCase());
        break;
      case 'pascal':
        result = text.toLowerCase()
          .replace(/[^a-zA-Z0-9]+(.)/g, (_, char) => char.toUpperCase())
          .replace(/^[a-z]/, (char) => char.toUpperCase());
        break;
      case 'snake':
        result = text.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        break;
      case 'kebab':
        result = text.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
        break;
      case 'alternate':
        result = text.split('').map((char, i) => 
          i % 2 === 0 ? char.toUpperCase() : char.toLowerCase()
        ).join('');
        break;
      case 'inverse':
        result = text.split('').map(char => 
          char === char.toUpperCase() ? char.toLowerCase() : char.toUpperCase()
        ).join('');
        break;
      default:
        result = text;
    }
    
    setText(result);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text);
    setCopied('all');
    setTimeout(() => setCopied(''), 2000);
  };

  const clearText = () => {
    setText('');
    setMisspelledWords([]);
    setHighlightedText('');
  };

  const toggleSpellCheck = () => {
    const newState = !spellCheckEnabled;
    setSpellCheckEnabled(newState);
    if (!newState) {
      setMisspelledWords([]);
      setHighlightedText('');
    } else {
      checkSpelling(text);
    }
  };

  const getStats = () => {
    return {
      characters: text.length,
      charactersNoSpaces: text.replace(/\s/g, '').length,
      words: text.trim() ? text.trim().split(/\s+/).length : 0,
      lines: text.split('\n').length,
      sentences: text.split(/[.!?]+/).filter(s => s.trim()).length
    };
  };

  const stats = getStats();

  return (
    <div className="tc-container">
      <div className="tc-input-section">
        <div className="tc-header">
          <label className="tc-label">Enter Your Text</label>
          <div className="tc-actions">
            <button 
              onClick={toggleSpellCheck} 
              className={`btn tc-action-btn ${spellCheckEnabled ? 'tc-spell-active' : ''}`}
              title="Toggle spell checker"
            >
              <span className="tc-btn-icon">{spellCheckEnabled ? '✓' : '✎'}</span>
              <span className="tc-btn-text">Spell Check {spellCheckEnabled ? 'ON' : 'OFF'}</span>
            </button>
            {text && (
              <>
                <button onClick={copyToClipboard} className="btn tc-action-btn">
                  {copied === 'all' ? '✓ Copied' : '📋 Copy'}
                </button>
                <button onClick={clearText} className="btn tc-action-btn tc-clear-btn">
                  🗑️ Clear
                </button>
              </>
            )}
          </div>
        </div>
        
        <div className="tc-textarea-wrapper">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Type or paste your text here..."
            rows="8"
            className="tc-textarea"
          />
          {spellCheckEnabled && highlightedText && (
            <div 
              className="tc-highlight-overlay"
              dangerouslySetInnerHTML={{ __html: highlightedText }}
            />
          )}
        </div>

        {spellCheckEnabled && misspelledWords.length > 0 && (
          <div className="tc-spell-info">
            <span className="tc-spell-label">⚠️ Possibly misspelled:</span>
            <span className="tc-misspelled-list">{misspelledWords.join(', ')}</span>
          </div>
        )}
      </div>

      {text && (
        <div className="tc-stats">
          <div className="tc-stat-card">
            <span className="tc-stat-value">{stats.characters}</span>
            <span className="tc-stat-label">Characters</span>
          </div>
          <div className="tc-stat-card">
            <span className="tc-stat-value">{stats.charactersNoSpaces}</span>
            <span className="tc-stat-label">No Spaces</span>
          </div>
          <div className="tc-stat-card">
            <span className="tc-stat-value">{stats.words}</span>
            <span className="tc-stat-label">Words</span>
          </div>
          <div className="tc-stat-card">
            <span className="tc-stat-value">{stats.lines}</span>
            <span className="tc-stat-label">Lines</span>
          </div>
        </div>
      )}

      <div className="tc-grid">
        <button onClick={() => convertCase('upper')} className="tc-convert-btn">
          <span className="tc-grid-icon">🔠</span>
          <span className="tc-grid-text">UPPERCASE</span>
        </button>
        <button onClick={() => convertCase('lower')} className="tc-convert-btn">
          <span className="tc-grid-icon">🔡</span>
          <span className="tc-grid-text">lowercase</span>
        </button>
        <button onClick={() => convertCase('title')} className="tc-convert-btn">
          <span className="tc-grid-icon">📝</span>
          <span className="tc-grid-text">Title Case</span>
        </button>
        <button onClick={() => convertCase('sentence')} className="tc-convert-btn">
          <span className="tc-grid-icon">📄</span>
          <span className="tc-grid-text">Sentence case</span>
        </button>
        <button onClick={() => convertCase('camel')} className="tc-convert-btn">
          <span className="tc-grid-icon">🐫</span>
          <span className="tc-grid-text">camelCase</span>
        </button>
        <button onClick={() => convertCase('pascal')} className="tc-convert-btn">
          <span className="tc-grid-icon">🅿️</span>
          <span className="tc-grid-text">PascalCase</span>
        </button>
        <button onClick={() => convertCase('snake')} className="tc-convert-btn">
          <span className="tc-grid-icon">🐍</span>
          <span className="tc-grid-text">snake_case</span>
        </button>
        <button onClick={() => convertCase('kebab')} className="tc-convert-btn">
          <span className="tc-grid-icon">🍢</span>
          <span className="tc-grid-text">kebab-case</span>
        </button>
        <button onClick={() => convertCase('alternate')} className="tc-convert-btn">
          <span className="tc-grid-icon">🔀</span>
          <span className="tc-grid-text">aLtErNaTe</span>
        </button>
        <button onClick={() => convertCase('inverse')} className="tc-convert-btn">
          <span className="tc-grid-icon">🔄</span>
          <span className="tc-grid-text">InVeRsE</span>
        </button>
      </div>
    </div>
  );
};

export default TextCaseConverter;